import { dayIndexOf, isValidCalendarDate, toDashed } from '@lib/datetime'

/**
 * Which day the itinerary opens on, mirroring iOS `ItineraryDaySelectionStore`
 * with the web's URL on top: the `?day=` param wins; during the trip a fresh
 * open lands on today; outside it, the last picked day for this trip comes
 * back, falling back to day 1. Pure: `today` is a dashed calendar date.
 */

/** The zero-based trip day `date` falls on, or null when the trip is undated or the date lies outside it. */
export const tripDayContaining = (
	date: string,
	startDate: string | null | undefined,
	dayCount: number,
): number | null => {
	if (!isValidCalendarDate(startDate) || !isValidCalendarDate(date)) return null
	const offset = dayIndexOf(date, toDashed(startDate))
	return offset >= 0 && offset < dayCount ? offset : null
}

export interface SelectedDayInput {
	startDate: string | null | undefined
	dayCount: number
	/** The raw `?day=` value (1-based), if any. */
	dayParam: string | null
	/** Today as a dashed calendar date in the viewer's zone. */
	today: string
	/** The zero-based day last picked for this trip, if any was stored. */
	remembered: number | null
}

/** The 1-based `?day=` value parsed to a zero-based index, clamped into the trip; null when absent or not a number. */
export const dayFromParam = (dayParam: string | null, dayCount: number): number | null => {
	if (dayParam === null || !/^\d+$/.test(dayParam)) return null
	const zeroBased = Number(dayParam) - 1
	return Math.min(Math.max(zeroBased, 0), Math.max(dayCount - 1, 0))
}

export const resolveSelectedDay = ({ startDate, dayCount, dayParam, today, remembered }: SelectedDayInput): number => {
	const fromParam = dayFromParam(dayParam, dayCount)
	if (fromParam !== null) return fromParam
	const todayIndex = tripDayContaining(today, startDate, dayCount)
	if (todayIndex !== null) return todayIndex
	if (remembered !== null && remembered >= 0 && remembered < dayCount) return remembered
	return 0
}
